// ---------------------------------------------------------------------------
// The campaign the wizard edits: its load, and the one gate every save asks (R17a3)
//
// The wizard opens an existing campaign with the empty draft every wizard
// starts with, and reads the campaign into it once GET /api/campaigns/:id and
// its leads answer. Until then, and for good when that load fails, the draft
// is not the campaign: sent, it would go over it as PUT /api/campaigns/:id with
// no offering, the placeholder step as the sequence, no reps and no accounts
// (found in R17b: a leave or Save in that window did exactly that, and a
// failed load was swallowed, the empty wizard shown for the campaign).
//
// So the page keeps where the load stands (`CampaignLoad`), reads the two
// answers with `readCampaignAnswers`, and every save asks `saveRefusal` first.
// Plain TypeScript.
// ---------------------------------------------------------------------------

/**
 * Where the load of the edited campaign stands. A new wizard has nothing to
 * load; an edited one is loading until both answers are read, then loaded, or
 * failed with the reason the page shows.
 */
export type CampaignLoad =
  | { state: 'new' }
  | { state: 'loading' }
  | { state: 'loaded' }
  | { state: 'failed'; reason: string };

/** How a wizard starts: loading the campaign it was opened for, or new. */
export const firstLoad = (campaignId?: string): CampaignLoad => (campaignId ? { state: 'loading' } : { state: 'new' });

/** Where the steps would be when the load failed (the campaign page's words for the same failure, R16a). */
export const LOAD_FAILED_TITLE = 'This campaign could not be loaded';

/** Save's title, and the header's note, while the campaign is not there to save. */
export const SAVE_WHILE_LOADING = 'Save waits until the campaign has loaded.';
export const SAVE_AFTER_FAILED_LOAD = 'The campaign could not be loaded, so there is nothing to save.';
export const LOADING_NOTE = 'Loading the campaign…';
export const NOT_LOADED_NOTE = 'Not loaded';

/**
 * What holds the steps while the edited campaign is not there, or null once it
 * is (and for a new campaign). The steps' own rules would run on the empty
 * draft every wizard starts with: under the spinner and under the failure card
 * they said "Enter a campaign name to continue." as a banner, with a disabled
 * Next and every stepper dot titled so, for a name field that is not on the
 * page (found in the R17a3 review). Held, the stepper's dots carry this
 * instead, and there is no banner and no Next (logic/steps.ts).
 */
export function stepsHeldBy(load: CampaignLoad): string | null {
  if (load.state === 'loading') return LOADING_NOTE;
  if (load.state === 'failed') return LOAD_FAILED_TITLE;
  return null;
}

/** What the wizard knows when it is about to send a draft. */
export interface SaveGate {
  /** The campaign the wizard was opened to edit; none for a new one. */
  campaignId?: string;
  load: CampaignLoad;
  /** The id of the draft about to be sent: the campaign read into it sets it (draftFromCampaign). */
  draftId?: string;
}

/**
 * Why this draft may not be sent, or null. The one place the wizard's saves
 * ask (the page's persistDraft: Save, the autosave, the leave, Next with a lead
 * search): an edited campaign is sent only once it has loaded and the draft is
 * the one read from it. A new campaign is never refused: its first save
 * creates it.
 */
export function saveRefusal(gate: SaveGate): string | null {
  if (!gate.campaignId) return null;
  if (gate.load.state === 'failed') return SAVE_AFTER_FAILED_LOAD;
  if (gate.load.state !== 'loaded' || !gate.draftId) return SAVE_WHILE_LOADING;
  return null;
}

/** One answer as the page got it: its status and body, or null when no answer came (no connection). */
export interface Answer {
  status: number;
  body: unknown;
}

/** When no answer came at all (the Overview's words for the same, dashboard-load.ts). */
export const NO_CONNECTION = 'The server could not be reached. Check your connection.';

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/** Why an answer is not a success: the server's own words, else its status, else that it could not be read. */
function reasonOf(answer: Answer | null): string {
  if (!answer) return NO_CONNECTION;
  const body = record(answer.body);
  const said = typeof body?.error === 'string' ? body.error.trim() : '';
  if (said) return said;
  if (answer.status < 200 || answer.status > 299) return `The server answered ${answer.status}.`;
  return body ? 'The server did not confirm it.' : "The server's answer could not be read.";
}

const succeeded = (answer: Answer | null): answer is Answer =>
  !!answer && answer.status >= 200 && answer.status <= 299 && record(answer.body)?.ok === true;

/** A read answer: its value, or why the campaign could not be loaded. */
export type Read<T> = { ok: true; value: T } | { ok: false; reason: string };

/** The campaign from GET /api/campaigns/:id, or why it could not be loaded. */
// biome-ignore lint/suspicious/noExplicitAny: the server's campaign document, read field by field by draftFromCampaign
export function readCampaignAnswer(answer: Answer | null): Read<any> {
  if (!succeeded(answer)) return { ok: false, reason: reasonOf(answer) };
  const campaign = record(record(answer.body)?.campaign);
  return campaign ? { ok: true, value: campaign } : { ok: false, reason: "The server's answer held no campaign." };
}

/**
 * Its leads from GET /api/campaigns/:id/leads, or why the campaign could not
 * be loaded. Leads that did not load fail the load: a draft shown without them
 * would replace them with whatever the person imports next (POST …/leads
 * replaces every lead of a draft) and prepare without them.
 */
// biome-ignore lint/suspicious/noExplicitAny: the server's leads, as the draft keeps them
export function readLeadsAnswer(answer: Answer | null): Read<any[]> {
  if (!succeeded(answer)) return { ok: false, reason: `Its leads could not be loaded: ${reasonOf(answer)}` };
  const leads = record(answer.body)?.leads;
  return Array.isArray(leads)
    ? { ok: true, value: leads }
    : { ok: false, reason: "Its leads could not be loaded: the server's answer held none." };
}
